// / <reference path="./SoccerPreGame.d.ts" />

import { useEffect, useState, Fragment } from 'react';

import getOr from '~/util/getOr';
import StyledComponent from '~/components/Styled/Styled';
import MatchupSection from '~/components/MatchupSection/MatchupSection';
import getMatchupStandings from '~/util/getMatchupStandings';

import css from './SoccerPreGame.scss';

type RowProps = {
  teamRanking: {
    ranking: number;
    change_in_rank: number;
    team: any;
    games_played: number;
    wins: number;
    ties: number;
    losses: number;
    points: number;
    goals_difference: number;
  };
};

const Row = ({ teamRanking }: RowProps) => {
  const increase = teamRanking.change_in_rank > 0;
  const decrease = teamRanking.change_in_rank < 0;

  return (
    <tr className={css.row}>
      <td className={css.ranking} headers={`${teamRanking.team.name}`}>
        {increase ? <div className={css.arrowUp} /> : null}
        {teamRanking.ranking}
        {decrease ? <div className={css.arrowDown} /> : null}
      </td>
      <th scope="row" id={`${teamRanking.team.name}`}>
        <div className={css.team}>
          <img
            className={css.small}
            src={teamRanking.team.logos.small}
            alt={`${teamRanking.team.name} logo`}
          />
          {teamRanking.team.full_name}
        </div>
      </th>
      <td className={css.stat}>{teamRanking.games_played}</td>
      <td className={css.stat}>{`${teamRanking.wins}-${teamRanking.ties}-${
        teamRanking.losses
      }`}</td>
      <td className={css.stat}>{teamRanking.points}</td>
      <td className={css.goalDifference}>{teamRanking.goals_difference}</td>
    </tr>
  );
};

type TableProps = {
  event: Event;
};

const MatchupTable = ({ event }: TableProps) => {
  const [standings, setStandings] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      let matchupStandings = await getMatchupStandings(
        event.league.slug,
        event.home_team.id,
        event.away_team.id
      );
      if (matchupStandings) {
        matchupStandings = matchupStandings.filter(standing => standing);
      }
      setStandings(matchupStandings);
    };

    fetchData();
  }, []);

  if (!standings) {
    return null;
  }

  return (
    <div className={css.root}>
      <h2 className={css.title}>Table</h2>
      <table className={css.table}>
        <thead>
          <tr className={css.tableHead}>
            <td colSpan={2} />
            <th>GP</th>
            <th>W-D-L</th>
            <th>P</th>
            <th className={css.goalDifference}>GD</th>
          </tr>
        </thead>
        {standings.map(teamRanking => <Row key={teamRanking.team.id} teamRanking={teamRanking} />)}
      </table>
    </div>
  );
};

type TeamFormValuesProps = {
  results: string;
};

const TeamFormValues = ({ results }: TeamFormValuesProps) => {
  const resultArray = results.split(',');

  return (
    <div className={css.teamFormValuesRow}>
      {resultArray.map(result => {
        let resultElem;

        switch (result) {
          case 'win':
            resultElem = <span className={`${css.teamFormCircle} ${css.win}`}>W</span>;
            break;
          case 'loss':
            resultElem = <span className={`${css.teamFormCircle} ${css.loss}`}>L</span>;
            break;
          case 'draw':
            resultElem = <span className={`${css.teamFormCircle} ${css.draw}`}>D</span>;
            break;
          default:
            resultElem = <span className={`${css.teamFormCircle} ${css.neutral}`}>-</span>;
            break;
        }

        return resultElem;
      })}
    </div>
  );
};

type Team = {
  team: any;
  last_results: string;
};

type TeamFormProps = {
  home: Team;
  away: Team;
};

const TeamForm = ({ home, away }: TeamFormProps) => (
  <MatchupSection>
    <div className={css.teamForm}>
      <h2 className={css.title}>Team Form</h2>
      <div className={css.teamFormValues}>
        <div>
          <img
            className={css.small}
            src={home.team.logos.small}
            alt={`${home.team.full_name} logo`}
          />
          <span>{home.team.full_name}</span>
        </div>
        <TeamFormValues results={home.last_results} />
      </div>
      <div className={css.teamFormValues}>
        <div>
          <img
            className={css.small}
            src={away.team.logos.small}
            alt={`${away.team.full_name} logo`}
          />
          <span>{away.team.full_name}</span>
        </div>
        <TeamFormValues results={away.last_results} />
      </div>
    </div>
  </MatchupSection>
);

type PreGameProps = {
  event: Event;
  isLoading: boolean;
  teamForm: string;
};

const SoccerPreGame = ({ event, isLoading, teamForm }: PreGameProps) => {
  const home = {
    team: event.home_team,
    last_results: getOr(event, 'standings.home.last_results', ''),
  };

  const away = {
    team: event.away_team,
    last_results: getOr(event, 'standings.away.last_results', ''),
  };

  return (
    <Fragment>
      {teamForm && !isLoading ? <TeamForm home={home} away={away} /> : null}
      {<MatchupTable event={event} />}
    </Fragment>
  );
};

export default StyledComponent(SoccerPreGame, [css]);
